import React, { useCallback, useMemo } from "react";
import "../home-styling/packages.css";

const Packages = ({
  packages,
  loading,
  viewPackageDetails,
  deletePackage
}) => {
  // Memoized utility functions
  const renderVerificationBadge = useCallback((isVerified) => {
    return isVerified ? (
      <span className="verified-badge verified" title="Verified Package">
        Verified
      </span>
    ) : (
      <span className="verified-badge not-verified" title="Pending Verification">
        Not Verified
      </span>
    );
  }, []);

  // Memoized event handlers
  const handleViewDetails = useCallback((packageId) => {
    viewPackageDetails(packageId);
  }, [viewPackageDetails]);

  const handleDeletePackage = useCallback((packageId) => {
    deletePackage(packageId);
  }, [deletePackage]);

  // Memoized package render function
  const renderPackageCard = useCallback((pkg) => {
    const packageLogo = pkg.logo_url || pkg.logoDataURL;
    const isValidImage = packageLogo && packageLogo.length > 0;
    const isVerified = pkg.is_verified === 1 || pkg.is_verified === true;

    return (
      <div key={pkg.id} className="package-card" data-package-id={pkg.id}>
        <div className="package-header">
          {renderVerificationBadge(isVerified)}
          <div className="package-title-section">
            <h3>{pkg.packageName || "Untitled Package"}</h3>
          </div>
        </div>

        <div className="package-con">
          <div className="package-logo-con">
            {isValidImage && (
              <div className="package-logo">
                <img
                  src={packageLogo}
                  alt={pkg.packageName || "Package Logo"}
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div className="package-content">
            <div className="package-meta">
              <span className="meta-item" style={{display:"flex",flexDirection:"column"}}>
                <strong>Location:</strong><div>{pkg.primaryLocation || "Location not set"}</div> 
              </span>
              <span className="meta-item">
                <strong>Number of People:</strong>{" "}
                {pkg.numPeople ? pkg.numPeople + " People" : "People not set"}
              </span>
              <span className="meta-item">
                <strong>Price:</strong> {pkg.price ? "₹" + pkg.price : "Price not set"}
              </span>
              {pkg.packageCategory && (
                <span className="meta-item">
                  <strong>Type:</strong> {pkg.packageCategory}
                </span>
              )}
            </div>

            <p className="package-description">
              <strong>Description:</strong> {pkg.shortDescription || "No description provided"}
            </p>

            <div className="package-actions">
              <button
                className="btn-view"
                onClick={() => handleViewDetails(pkg.id)}
                aria-label={`View details for ${pkg.packageName || 'package'}`}
              >
                View Details
              </button>
              <button
                className="btn-delete"
                onClick={() => handleDeletePackage(pkg.id)}
                aria-label={`Delete ${pkg.packageName || 'package'}`}
              >
                Delete
              </button>
            </div>

            <div className="package-footer">
              <small>
                <strong>Created:</strong>{" "}
                {pkg.createdAt
                  ? new Date(pkg.createdAt).toLocaleDateString()
                  : "Unknown date"}
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  }, [renderVerificationBadge, handleViewDetails, handleDeletePackage]);

  // Memoized packages render function
  const renderPackages = useMemo(() => {
    if (loading.packages) {
      return (
        <div className="loading" aria-live="polite">
          Loading packages...
        </div>
      );
    }

    if (packages.length === 0) {
      return (
        <div className="no-packages" aria-live="polite">
          <p>No packages created yet.</p>
        </div>
      );
    }

    return packages.map(renderPackageCard);
  }, [packages, loading.packages, renderPackageCard]);

  return (
    <div>
      <h2 style={{ marginBottom: "20px" }}>
        My Packages ({packages.length})
      </h2>
      <div id="packagesContainer" aria-live="polite">
        {renderPackages}
      </div>
    </div>
  );
};

export default React.memo(Packages);